import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import "@/xplosiv.css";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { Toaster } from "sonner";
import Navbar from "@/components/layout/XplosivNavbar";
import Footer from "@/components/layout/XplosivFooter";
import ScrollToTop from "@/components/layout/ScrollToTop";
import CartDrawer from "@/components/storefront/CartDrawer";
import Home from "@/pages/XplosivHome";
import Shop from "@/pages/EventExperiences";
import ProductDetail from "@/pages/EventExperienceDetail";
import EventServices from "@/pages/EventServices";
import EventServiceOccasions from "@/pages/EventServiceOccasions";
import OccasionDetail from "@/pages/OccasionDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmed from "@/pages/OrderConfirmed";
import OrderTrack from "@/pages/OrderTrack";
import Reels from "@/pages/Reels";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import Wishlist from "@/pages/Wishlist";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Lookbook from "@/pages/Lookbook";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/Products";
import AdminOccasions from "@/pages/admin/Occasions";
import AdminProductForm from "@/pages/admin/ProductForm";
import AdminInventory from "@/pages/admin/Inventory";
import AdminOrders from "@/pages/admin/Orders";
import AdminBanners from "@/pages/admin/Banners";
import AdminReels from "@/pages/admin/Reels";
import AdminCoupons from "@/pages/admin/Coupons";
import AdminCustomers from "@/pages/admin/Customers";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminBlog from "@/pages/admin/Blog";
import AdminReviews from "@/pages/admin/Reviews";
import AdminSiteContent from "@/pages/admin/SiteContent";
import AdminShipments from "@/pages/admin/Shipments";
import AdminCategories from "@/pages/admin/Categories";
import AdminSettings from "@/pages/admin/Settings";
import AdminCollections from "@/pages/admin/Collections";
import AdminAttributes from "@/pages/admin/Attributes";
import AdminReturns from "@/pages/admin/Returns";
import AdminReportsSales from "@/pages/admin/ReportsSales";
import AdminReportsInventory from "@/pages/admin/ReportsInventory";
import { AdminSupport, AdminLoyalty, AdminCampaigns, AdminMenus, AdminSuppliers, AdminPurchaseOrders, AdminRoles, AdminActivity } from "@/pages/admin/AdminOps";
import ComingSoon from "@/pages/admin/ComingSoon";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import { PrivacyPage, TermsPage, CookiesPage, ShippingPage, ReturnsPage, SizingPage, FAQPage } from "@/pages/Help";
import { ProtectedAdmin } from "@/components/auth/ProtectedRoute";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

function Layout({ children }) {
    return (
        <>
            <Navbar />
            <main className="relative z-[2] pt-[100px]">{children}</main>
            <Footer />
            <CartDrawer />
        </>
    );
}

function App() {
    return (
        <div className="App">
            <HelmetProvider>
                <AuthProvider>
                    <CartProvider>
                        <BrowserRouter>
                            <WishlistProvider>
                            <ScrollToTop />
                            <Toaster position="top-center" richColors closeButton />
                        <Routes>
                            {/* admin routes (no layout) — GUARDED: must be logged in as admin */}
                            <Route element={<ProtectedAdmin />}>
                                <Route path="/admin" element={<AdminLayout />}>
                                <Route index element={<AdminDashboard />} />
                                <Route path="products" element={<AdminProducts />} />
                                <Route path="products/new" element={<AdminProductForm />} />
                                <Route path="products/:id/edit" element={<AdminProductForm />} />
                                <Route path="occasions" element={<AdminOccasions />} />
                                <Route path="inventory" element={<AdminInventory />} />
                                <Route path="orders" element={<AdminOrders />} />
                                <Route path="banners" element={<AdminBanners />} />
                                <Route path="reels" element={<AdminReels />} />
                                <Route path="coupons" element={<AdminCoupons />} />
                                <Route path="customers" element={<AdminCustomers />} />
                                <Route path="analytics" element={<AdminAnalytics />} />
                                <Route path="blog" element={<AdminBlog />} />
                                <Route path="reviews" element={<AdminReviews />} />
                                <Route path="site-content" element={<AdminSiteContent />} />
                                <Route path="shipments" element={<AdminShipments />} />
                                <Route path="categories" element={<AdminCategories />} />
                                <Route path="settings" element={<AdminSettings />} />
                                <Route path="collections" element={<AdminCollections />} />
                                <Route path="attributes" element={<AdminAttributes />} />
                                <Route path="returns" element={<AdminReturns />} />
                                <Route path="reports/sales" element={<AdminReportsSales />} />
                                <Route path="reports/inventory" element={<AdminReportsInventory />} />
                                <Route path="support" element={<AdminSupport />} />
                                <Route path="loyalty" element={<AdminLoyalty />} />
                                <Route path="campaigns" element={<AdminCampaigns />} />
                                <Route path="menus" element={<AdminMenus />} />
                                <Route path="seo" element={<ComingSoon title="SEO" description="Sitemap and robots.txt are auto-served at /api/sitemap.xml and /api/robots.txt. Per-page meta tags are rendered by SEO helper (react-helmet-async)." />} />
                                <Route path="suppliers" element={<AdminSuppliers />} />
                                <Route path="purchase-orders" element={<AdminPurchaseOrders />} />
                                <Route path="roles" element={<AdminRoles />} />
                                <Route path="activity" element={<AdminActivity />} />
                            </Route>
                            </Route>
                            {/* public routes */}
                            <Route path="/" element={<Layout><Home /></Layout>} />
                            <Route path="/shop" element={<Layout><Shop /></Layout>} />
                            <Route path="/shop/:category" element={<Layout><Shop /></Layout>} />
                            <Route path="/services" element={<Layout><EventServices /></Layout>} />
                            <Route path="/services/:serviceSlug" element={<Layout><EventServiceOccasions /></Layout>} />
                            <Route path="/occasions/:slug" element={<Layout><OccasionDetail /></Layout>} />
                            <Route path="/experiences" element={<Layout><Shop /></Layout>} />
                            <Route path="/products" element={<Layout><Shop productsMode /></Layout>} />
                            <Route path="/product/:slug" element={<Layout><ProductDetail /></Layout>} />
                            <Route path="/cart" element={<Layout><Cart /></Layout>} />
                            <Route path="/checkout" element={<Layout><Checkout /></Layout>} />
                            <Route path="/order/confirmed/:orderId" element={<Layout><OrderConfirmed /></Layout>} />
                            <Route path="/track/:orderNumber" element={<Layout><OrderTrack /></Layout>} />
                            <Route path="/reels" element={<Layout><Reels /></Layout>} />
                            <Route path="/login" element={<Layout><Login /></Layout>} />
                            <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
                            <Route path="/reset-password" element={<Layout><ResetPassword /></Layout>} />
                            <Route path="/register" element={<Layout><Register /></Layout>} />
                            <Route path="/account" element={<Layout><Account /></Layout>} />
                            <Route path="/wishlist" element={<Layout><Wishlist /></Layout>} />
                            <Route path="/about" element={<Layout><About /></Layout>} />
                            <Route path="/contact" element={<Layout><Contact /></Layout>} />
                            <Route path="/lookbook" element={<Layout><Lookbook /></Layout>} />
                            <Route path="/blog" element={<Layout><Blog /></Layout>} />
                            <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />
                            <Route path="/help/privacy" element={<Layout><PrivacyPage /></Layout>} />
                            <Route path="/help/terms" element={<Layout><TermsPage /></Layout>} />
                            <Route path="/help/cookies" element={<Layout><CookiesPage /></Layout>} />
                            <Route path="/help/shipping" element={<Layout><ShippingPage /></Layout>} />
                            <Route path="/help/returns" element={<Layout><ReturnsPage /></Layout>} />
                            <Route path="/help/sizing" element={<Layout><SizingPage /></Layout>} />
                            <Route path="/help/faq" element={<Layout><FAQPage /></Layout>} />
                        </Routes>
                            </WishlistProvider>
                        </BrowserRouter>
                    </CartProvider>
                </AuthProvider>
            </HelmetProvider>
        </div>
    );
}

export default App;
